// Geometría del cohete: perfiles, diámetros y disposición axial. Ver docs/theory/rocket-model.md.
import type { Attached, NoseCone, Placement, Rocket, StackComponent, Transition } from './types';

/** Radio del cono de ojiva a distancia `x` de la punta. */
export function noseRadius(nose: NoseCone, x: number): number {
  const L = nose.length;
  const R = nose.baseDiameter / 2;
  const xi = Math.min(Math.max(x / L, 0), 1);
  switch (nose.shape) {
    case 'conical':
      return R * xi;
    case 'elliptical':
      return R * Math.sqrt(Math.max(0, 1 - (1 - xi) ** 2));
    case 'parabolic':
      return R * (2 * xi - xi * xi);
    case 'power':
      return R * Math.pow(xi, nose.powerExponent ?? 0.5);
    case 'ogive': {
      const rho = (R * R + L * L) / (2 * R);
      const d = L - xi * L;
      return Math.sqrt(Math.max(0, rho * rho - d * d)) + R - rho;
    }
  }
}

/** Radio exterior de un componente de la pila a distancia `x` de su extremo delantero. */
export function stackRadius(c: StackComponent, x: number): number {
  switch (c.type) {
    case 'nose-cone':
      return noseRadius(c, x);
    case 'body-tube':
      return c.outerDiameter / 2;
    case 'transition': {
      const f = c.length === 0 ? 0 : Math.min(Math.max(x / c.length, 0), 1);
      return (c.foreDiameter / 2) * (1 - f) + (c.aftDiameter / 2) * f;
    }
  }
}

export const maxDiameter = (c: StackComponent): number =>
  c.type === 'nose-cone'
    ? c.baseDiameter
    : c.type === 'body-tube'
      ? c.outerDiameter
      : Math.max(c.foreDiameter, c.aftDiameter);

/** Volumen de revolución del perfil exterior (m³), por la regla de Simpson. */
export function profileVolume(radius: (x: number) => number, length: number, slices = 400): number {
  const n = slices % 2 === 0 ? slices : slices + 1;
  const h = length / n;
  let sum = 0;
  for (let i = 0; i <= n; i++) {
    const r = radius(i * h);
    const w = i === 0 || i === n ? 1 : i % 2 === 1 ? 4 : 2;
    sum += w * Math.PI * r * r;
  }
  return (sum * h) / 3;
}

export interface Layout {
  readonly component: StackComponent;
  /** Posición del extremo delantero (m desde la punta). */
  readonly x0: number;
  /** Posición del extremo trasero. */
  readonly x1: number;
}

export function layoutStack(rocket: Rocket): Layout[] {
  let x = 0;
  return rocket.stack.map((component) => {
    const x0 = x;
    x += component.length;
    return { component, x0, x1: x };
  });
}

export const totalLength = (rocket: Rocket): number =>
  rocket.stack.reduce((s, c) => s + c.length, 0);

/** Diámetro de referencia: el mayor diámetro exterior de la pila. */
export const referenceDiameter = (rocket: Rocket): number =>
  rocket.stack.reduce((m, c) => Math.max(m, maxDiameter(c)), 0);

/** Longitud axial del componente adjunto (m). */
export const attachedLength = (a: Attached): number =>
  a.type === 'fin-set' ? a.rootChord : a.type === 'parachute' ? a.packedLength : a.length;

/** Posición absoluta del borde delantero de un componente adjunto. */
export function attachedFront(parent: Layout, length: number, p: Placement): number {
  return p.anchor === 'front' ? parent.x0 + p.offset : parent.x1 - p.offset - length;
}

export const childrenOf = (c: StackComponent): readonly Attached[] =>
  c.type === 'nose-cone' ? [] : c.children;

export const isTransition = (c: StackComponent): c is Transition => c.type === 'transition';

/** Radio exterior del cuerpo (padre) en la posición axial absoluta `x`. */
export function bodyRadiusAt(parent: Layout, x: number): number {
  return stackRadius(parent.component, Math.min(Math.max(x - parent.x0, 0), parent.component.length));
}
